import { Navigate, useLocation, useNavigate, useRoutes } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import React, { useEffect } from 'react';

import DashboardLayout from '../layout/dashboard';
import Login from '../pages/auth/login';
import NotFound from '../pages/404';
import { RootState } from '../store';
import { useEffectiveSettings, SETTING } from '../hooks/use-effective-settings';
import { SystemUserListScreen } from '../pages/app/system-user/list';
import { DeviceListScreen } from '../pages/app/devices/list';
import { AlertListScreen } from '../pages/app/alerts/list';
import { EventListScreen } from '../pages/app/events/list';
import { CustomerRegistrationOnboardingScreen } from '../pages/onboarding/customer-registration';
import { UserListScreen } from '../pages/app/users/list';
import { CustomerListScreen } from '../pages/app/customers/list';
import { AuthService } from '../services/auth.service';
import { getCurrentUser } from '../services/cognito.service';
import { SubscriptionListScreen } from '../pages/app/subscriptions/list';
import { ProfileScreen } from '../pages/app/profile';
import { UserFirstLogin } from '../pages/auth/user-first-login';
import { ForgotPasswordScreen } from '../pages/auth/forgot-password';
import ResetPasswordScreen from '../pages/auth/reset-password';
import { MessageCenterScreen } from '../pages/app/messageCenter';
import drawOnMapConstants from '../components/event-map/draw-on-map/drawOnMapConstants';
import { DrawOnMapScreen } from '../components/event-map/draw-on-map/drawOnMapScreen';
import { CreateSystemUserScreen } from '../pages/app/system-user/create/create-system-user.screen';
import { EditSystemUserScreen } from '../pages/app/system-user/edit/edit-system-user.screen';
import { SystemUserBulkUploadScreen } from '../pages/app/system-user/bulk-upload';
import { SettingsScreen } from '../pages/app/settings/settings.screen';
import { NotificationsPage } from '../pages/app/notifications/NotificationsPage';
import { AppointmentsListScreen } from '../pages/app/appointments/appointments-list.screen';
import { CreateCustomerScreen } from '../pages/app/customers/create/create-customer.screen';
import { ReportListScreen } from '../pages/app/reports/list';
import { TeamFormPage } from '../pages/app/groups-and-teams/teams/team-form.page';
import { TestModeScreen } from '../pages/app/test-mode';
import { AccountDeletionRequestsScreen } from '../pages/app/account-deletion-requests';
import { MyDashboardPage } from '../pages/app/my-dashboard/MyDashboardPage';
import { SafetyReport } from "../pages/app/reports/safety-report";
import { ListReports } from "../pages/app/reports/list/list-reports";
import { ScheduleListScreen } from '../components/schedules/schedules';
import { RiskReview } from "../pages/app/reports/risk-review";
import { BulkSafetyReportScreen } from "../pages/app/reports/bulk-safety-report";
import { GroupsAndTeamsListPage } from '../pages/app/groups-and-teams/groups-and-teams-list';
import { GroupFormPage } from '../pages/app/groups-and-teams/group-form.page';
import { GroupsTeamsPage } from '../pages/app/groups-teams/GroupsTeamsPage';
import { TeamsInGroupPage } from '../pages/app/groups-teams/TeamsInGroupPage';
import { TeamDetailPage } from '../pages/app/groups-teams/TeamDetailPage';
import { TagExplorerPage } from '../pages/app/groups-teams/TagExplorerPage';
import { permissionCheck, isIntakeOnlyUser } from '../helpers/permissionCheck';
import { customerPermissionFlags } from '../constants/systemPermissions.constants';
import { AuthUserPage } from '../pages/app/auth-user';
import { EditCustomerSettingsScreen } from '../pages/app/customers/edit/edit-customer-settings.screen';
import { UserActivity } from "../pages/app/reports/user-activity";
import { NewMessageRecipientsScreen } from '../pages/app/messageCenter/new-message-recipients.screen';
import { NewMessageComposeScreen } from '../pages/app/messageCenter/new-message-compose.screen';
import { EditMessageRecipientsScreen } from '../pages/app/messageCenter/edit-message-recipients.screen';
import { EditMessageComposeScreen } from '../pages/app/messageCenter/edit-message-compose.screen';
import {RiskAssessment} from "../pages/app/reports/riskAssessment";
import { AutomatedReminderRecipientsScreen } from '../pages/app/messageCenter/automated-reminders/automated-reminders-recipients.screen';
import { EditAutomatedReminderRecipientsScreen } from '../pages/app/messageCenter/automated-reminders/edit-automated-reminders-recipients.screen';
import { AutomatedReminderConfigureScreen } from '../pages/app/messageCenter/automated-reminders/automated-reminders-configure.screen';
import { EditAutomatedReminderConfigureScreen } from '../pages/app/messageCenter/automated-reminders/edit-automated-reminders-configure.screen';
import { MessageHistoryScreen } from '../pages/app/messageCenter/message-history/message-history-list.screen';
import { GroupsTeamsActivityDashboard } from '../pages/app/reports/groups-teams-activity';
import { BannerListScreen } from '../pages/app/banners/list';
import { CreateBannerScreen } from '../pages/app/banners/create/create-banner.screen';
import { EditBannerScreen } from '../pages/app/banners/edit/edit-banner.screen';
import { BulkActionsLandingPage } from '../pages/app/bulk-actions/landing-page';
import { BulkDeleteUsersScreen } from '../pages/app/bulk-actions/delete-users';
import { BulkUpdateUsersScreen } from '../pages/app/bulk-actions/update-users';
import { FieldSafetyReportsListScreen } from '../pages/app/field-safety-reports/list';
import { PomMasterSettingsScreen } from '../pages/app/pom-master-settings';
import { AuditLogExplorerScreen } from '../pages/app/audit-log-explorer';
import { SupportAgentScreen } from '../pages/app/support-agent';
import { DeprovisionedUsersScreen } from '../pages/app/reports/deprovisioned-users';
import { ScheduledReportsListScreen } from '../pages/app/reports/scheduled-reports';
import { ScheduledReportFormScreen } from '../pages/app/reports/scheduled-reports/form.screen';
import { Loading } from '../components';
import { ApiAccessScreen } from '../pages/app/api-access';
import { safeRedirectPath } from '../helpers/safeRedirectPath';


// Redirect unknown routes to the dashboard, which lives at '/'
function RedirectToDashboard() {
    const navigate = useNavigate();
    useEffect(() => {
        navigate('/');
        // must return some value not undefined, null works
    }, []);

    return null;
}

// Logged-out visit to a path that needs to survive login (e.g. a Zendesk
// ticket's deep link into a specific chat, ClickUp 868mb93wj) — sends to
// /login with the originally-requested path+search in router state, so
// StepLoginForm's success handler can return here instead of always
// landing on '/'. Only wired for paths that actually need this (see
// UNAUTHENTICATED_ROUTES below) — every other unauthenticated path keeps
// its existing behavior unchanged.
function RedirectToLoginPreservingPath() {
    const location = useLocation();
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
}

const AppRoutes = () => {
    const { authUser, token } = useSelector((state: RootState) => state.auth);
    const { details, selectedSubscription } = useSelector((state: RootState) => state.customer);
    const navigate = useNavigate();
    const location = useLocation();
    const IS_AUTHENTICATED = !!authUser && !!token;
    const sessionScopeKey = [
        authUser?.id || 'anonymous',
        authUser?.customerId || details?.customer?.id || 'no-customer',
        authUser?.subscriptionId || selectedSubscription?.id || 'no-subscription',
        authUser?.masquarade ? 'masquarade' : 'direct',
    ].join(':');

    useEffect(() => {
        let ignore = false;
        const controller = new AbortController();

        (async () => {
            try {
                const user = (await getCurrentUser()) as any;
                if (ignore) {
                    return;
                }

                if (user && !authUser.id) {
                    await AuthService.sso(
                        user.email,
                        user.token,
                        user.name,
                        'Google',
                        (isValid, err) => {
                            if (ignore) {
                                return;
                            }

                            if (!isValid) {
                                toast(err, {
                                    autoClose: 1500,
                                    type: 'error',
                                });
                                return;
                            }

                            toast('Login successfully!', {
                                autoClose: 1500,
                                type: 'success',
                            });

                            // Same `state.from` passthrough as the other
                            // login paths (Pollen review, round 4, Low item)
                            // — a Cognito/Google SSO login triggered from a
                            // deep link should also land back where it came
                            // from instead of always the dashboard.
                            const from = safeRedirectPath((location.state as { from?: string } | null)?.from);
                            navigate(from || '/');
                        },
                        controller.signal
                    );
                }
            } catch (err) { }
        })();

        return () => {
            ignore = true;
            controller.abort();
        };
    }, []);

    const ONBOARDING = 'onboarding';

    const FIRST_LOGIN_ROUTES = {
        path: '/',
        children: [
            { element: <Navigate to="/" replace /> },
            { path: '/', element: <UserFirstLogin /> },
        ],
    };

    const POM_MASTER_AUTHENTICATED_ROUTES = {
        path: '/',
        element: <DashboardLayout key={`dashboard-${sessionScopeKey}`} />,
        children: [
            { element: <Navigate to="/" replace /> },
            { path: '/', element: <CustomerListScreen /> },
            { path: '/customers', element: <CustomerListScreen /> },
            { path: '/customers/create', element: <CreateCustomerScreen /> },
            { path: '/customers/:id/edit', element: <EditCustomerSettingsScreen /> },
            { path: '/system-users', element: <SystemUserListScreen /> },
            { path: '/system-users/create', element: <CreateSystemUserScreen /> },
            { path: '/system-users/bulk-upload', element: <SystemUserBulkUploadScreen /> },
            { path: '/profile', element: <ProfileScreen /> },
            { path: '/system-users/:id/edit', element: <EditSystemUserScreen /> },
            { path: '*', element: <RedirectToDashboard /> },
            {
                path: 'auth-codes',
                element: <AuthUserPage />,
            },
            { path: '/banners', element: <BannerListScreen /> },
            { path: '/banners/create', element: <CreateBannerScreen /> },
            { path: '/banners/:id/edit', element: <EditBannerScreen /> },
            { path: '/pom-master/settings', element: <PomMasterSettingsScreen /> },
            { path: '/audit-log-explorer', element: <AuditLogExplorerScreen /> },
            { path: '/support-agent', element: <SupportAgentScreen /> },
        ],
    };

    const { getSetting, isLoading: settingsLoading } = useEffectiveSettings();
    const showInsightsDashboard = getSetting(SETTING.showInsightsDashboard, true);
    const showRiskAssessment = getSetting(SETTING.showRiskAssessment, false);
    const showGroupsTeamsDashboard = getSetting(SETTING.showGroupsTeamsDashboard, false);
    const showTestMode = getSetting(SETTING.showTestMode, false);
    const showNotificationRules = getSetting(SETTING.notificationRules, false);
    const isIraStandalone = getSetting<string>(SETTING.organizationType, 'full') === 'iraStandalone';
    // A per-user intake specialist (sole access is the IRA capability) gets the
    // exact same locked-down, IRA-only routing as a standalone org. This is
    // derived from the JWT's permissionFlags, so unlike isIraStandalone it is
    // known immediately and does not depend on settings loading.
    const isIntakeOnly = isIntakeOnlyUser(authUser);
    const restrictToIra = isIraStandalone || isIntakeOnly;

    // IRA-standalone route gating is DEFAULT-DENY: every customer route below
    // is guarded unless its path is allowlisted here, so routes added later
    // are gated automatically. Guarded routes hold on a loader while settings
    // resolve instead of failing open to the full platform (a deep link must
    // never mount, query, and flash a screen the org doesn't have).
    const STANDALONE_ALLOWED_PATHS = new Set<string>([
        '/',                              // handles its own standalone redirect
        '/risk-assessment',               // the product
        '/api-access',                    // self-serve / sales-gated API page
        '/profile',                       // account/password management
        // deliberate: the admin email links straight here, and the backend
        // already restricts these endpoints to ADMIN/SUPER_ADMIN
        '/account-deletion-requests',
        '/reports/safety-report',         // redirect stub to /risk-assessment
        // A masquerading POM Master (not a standalone customer) can land
        // here while impersonating a standalone/IRA-only org — the page
        // itself (SupportAgentScreen) does its own pomMaster/masquerade
        // checks and must not be blocked by org-type gating first, or a
        // Zendesk deep link into a chat under a standalone org silently
        // redirects to '/' instead (Pollen review, PR #540, critical item 1).
        '/support-agent',
        '*',                              // not-found handler
    ]);
    const applyStandaloneGating = (route: any) =>
        (!route.path || STANDALONE_ALLOWED_PATHS.has(route.path))
            ? route
            : {
                ...route,
                element: settingsLoading
                    ? <Loading />
                    : restrictToIra
                        ? <Navigate to="/" replace />
                        : route.element,
            };

    const CUSTOMER_AUTHENTICATED_ROUTES = {
        path: '/',
        element: <DashboardLayout key={`dashboard-${sessionScopeKey}`} />,
        children: ([
            { element: <Navigate to="/" replace /> },
            // Standalone orgs land on Risk Assessment; the Loading gate keeps a
            // standalone user from seeing a dashboard flash while settings load.
            { path: '/', element: settingsLoading ? <Loading /> : restrictToIra ? <Navigate to="/risk-assessment" replace /> : showInsightsDashboard ? <MyDashboardPage /> : <EventListScreen /> },
            { path: '/my-dashboard', element: showInsightsDashboard ? <MyDashboardPage /> : <EventListScreen /> },
            { path: '/event-map', element: <EventListScreen /> },
            { path: '/users', element: <UserListScreen /> },
            { path: '/appointments', element: <AppointmentsListScreen /> },
            { path: '/schedules', element: <ScheduleListScreen /> },
            { path: '/system-users', element: <SystemUserListScreen /> },
            { path: '/devices', element: <DeviceListScreen /> },
            { path: '/alerts', element: <AlertListScreen /> },
            { path: '/test-mode', element: (showTestMode || settingsLoading) ? <TestModeScreen /> : <Navigate to="/" replace /> },
            // Not gated on the setting: the admin email links straight here, and
            // the backend already restricts these endpoints to ADMIN/SUPER_ADMIN.
            { path: '/account-deletion-requests', element: <AccountDeletionRequestsScreen /> },
            { path: '/events', element: <EventListScreen /> },
            { path: '/subscriptions', element: <SubscriptionListScreen /> },
            { path: '/profile', element: <ProfileScreen /> },
            { path: '/settings', element: <SettingsScreen /> },
            { path: '/settings/notifications', element: (showNotificationRules || settingsLoading) ? <NotificationsPage /> : <Navigate to="/settings" replace /> },
            { path: '/messages', element: <MessageCenterScreen /> },
            { path: '/messages/new/recipients', element: <NewMessageRecipientsScreen /> },
            { path: '/messages/new/compose', element: <NewMessageComposeScreen /> },
            { path: '/messages/:id/edit/recipients', element: <EditMessageRecipientsScreen /> },
            { path: '/messages/:id/edit/compose', element: <EditMessageComposeScreen /> },
            { path: '/messages/automated-reminders/recipients', element: <AutomatedReminderRecipientsScreen /> },
            { path: '/messages/automated-reminders/configure', element: <AutomatedReminderConfigureScreen /> },
            { path: '/messages/automated-reminders/:id/edit/recipients', element: <EditAutomatedReminderRecipientsScreen /> },
            { path: '/messages/automated-reminders/:id/edit/configure', element: <EditAutomatedReminderConfigureScreen /> },
            { path: '/messages/history', element: <MessageHistoryScreen /> },
            { path: '/system-users/create', element: <CreateSystemUserScreen /> },
            { path: '/system-users/bulk-upload', element: <SystemUserBulkUploadScreen /> },
            { path: '/system-users/:id/edit', element: <EditSystemUserScreen /> },
            // isIraStandalone keeps this reachable even if a POM master toggles
            // the IRA feature setting off for a standalone org — without it,
            // '/' and '/risk-assessment' redirect at each other forever.
            { path: '/risk-assessment', element: (showRiskAssessment || restrictToIra || settingsLoading) ? <RiskAssessment /> : <Navigate to="/" replace /> },
            { path: '/api-access', element: <ApiAccessScreen /> },
            // Pollen review, PR #540, critical item 1: this route previously
            // existed ONLY in POM_MASTER_AUTHENTICATED_ROUTES
            // (pomMaster && !masquarade), so a masquerading POM Master or a
            // non-POM-Master customer following a Zendesk deep link into a
            // chat hit '*' -> RedirectToDashboard before SupportAgentScreen
            // ever mounted — its own non-POM-Master message and
            // auto-stop-masquerade effect were unreachable dead code.
            // SupportAgentScreen does its own pomMaster/masquerade checks, so
            // mounting it here is safe for every visitor in this route tree.
            { path: '/support-agent', element: <SupportAgentScreen /> },
            // IRA moved out of Reports (2026-08): the old URL lives on in bookmarks,
            // emails, and insights CTA links, so keep it as a redirect while the flag is on.
            {...showRiskAssessment ? {path: '/reports/safety-report', element: <Navigate to="/risk-assessment" replace />}:{}},
            {
                path: '/reports', element: <ReportListScreen />,
                children: [
                    {...showRiskAssessment ? {} : {path: '/reports/safety-report', element: <SafetyReport />}},
                    { path: '/reports/', element: <ListReports /> },
                    { path: '/reports/field-safety-reports', element: <FieldSafetyReportsListScreen /> },
                    { path: '/reports/deprovisioned-users', element: <DeprovisionedUsersScreen /> },
                    { path: '/reports/scheduled-reports', element: <ScheduledReportsListScreen /> },
                    { path: '/reports/scheduled-reports/create', element: <ScheduledReportFormScreen /> },
                    { path: '/reports/scheduled-reports/:id/edit', element: <ScheduledReportFormScreen /> },
                    { path: '/reports/visit-risk-review', element: <RiskReview /> },
                    { path: '/reports/bulk-safety-report', element: <BulkSafetyReportScreen /> },
                    { path: '/reports/user-activity', element: <UserActivity /> },
                    { path: '/reports/groups-teams-activity', element: (showGroupsTeamsDashboard || settingsLoading) ? <GroupsTeamsActivityDashboard /> : <Navigate to="/reports/" replace /> }
                ]
            },
            {
                path: '/teams/create',
                element: <TeamFormPage />
            },
            {
                path: '/teams/:teamId/edit',
                element: <TeamFormPage />
            },
            {
                path: '/groups-teams',
                element: <GroupsTeamsPage />,
            },
            {
                path: '/groups-teams/tag-explorer',
                element: permissionCheck(customerPermissionFlags.GROUP_LEADER)
                    ? <TagExplorerPage />
                    : <Navigate to="/groups-teams" replace />,
            },
            {
                path: '/groups-teams/:groupId',
                element: <TeamsInGroupPage />,
            },
            {
                path: '/groups-teams/:groupId/:teamId',
                element: <TeamDetailPage />,
            },
            {
                path: '/groups-and-teams',
                element: <GroupsAndTeamsListPage />,
            },
            {
                path: '/groups/create',
                element: <GroupFormPage />,
            },
            {
                path: '/groups/:groupId/edit',
                element: <GroupFormPage />,
            },
            // Geofence editor: POM Master masquerade sessions only.
            ...(authUser?.masquarade
                ? [{ path: drawOnMapConstants.drawOnMapPath, element: <DrawOnMapScreen /> }]
                : []),
            
            { path: '/bulk-actions', element: <BulkActionsLandingPage /> },
            { path: '/bulk-actions/delete-users', element: <BulkDeleteUsersScreen /> },
            { path: '/bulk-actions/update-users', element: <BulkUpdateUsersScreen /> },
            { path: '*', element: <RedirectToDashboard /> },
        ] as any[]).map(applyStandaloneGating),
    };

    const UNAUTHENTICATED_ROUTES = {
        path: '/',
        children: [
            { element: <Navigate to="/login" replace /> },
            { path: '', element: <Login /> },
            { path: 'login', element: <Login /> },
            { path: '/recover-password', element: <ForgotPasswordScreen /> },
            { path: '/reset-password', element: <ResetPasswordScreen /> },
            // Deep link into a specific chat (ClickUp 868mb93wj) — send to
            // login, then back here, instead of the generic 404 every other
            // unauthenticated path still gets below.
            { path: '/support-agent', element: <RedirectToLoginPreservingPath /> },
            { path: '*', element: <NotFound /> },
            {
                path: `${ONBOARDING}/:uuid`,
                element: <CustomerRegistrationOnboardingScreen />,
            },
        ],
    };

    const AUTHENTICATED_ROUTES = authUser.pomMaster && !authUser.masquarade
        ? POM_MASTER_AUTHENTICATED_ROUTES
        : CUSTOMER_AUTHENTICATED_ROUTES;

    return useRoutes([
        IS_AUTHENTICATED
            ? authUser?.isFirstLogin
                ? FIRST_LOGIN_ROUTES
                : AUTHENTICATED_ROUTES
            : UNAUTHENTICATED_ROUTES,
    ]);
};

export default AppRoutes;
